<script setup lang="ts">
import { Link, router } from "@inertiajs/vue3"
import { reactive, ref } from "vue"
import { CalendarDaysIcon, EyeIcon } from "@heroicons/vue/24/outline"
import AppShell from "../Layout/AppShell.vue"
import { UiButton, UiDataTable, UiDrawer, UiTimeline } from "@/components"
import { useConfirmation } from "@/composables/useConfirmation"
import { useNotifications } from "@/composables/useNotifications"
import type { UiDataTableColumn, UiDataTableFilter } from "@/types/ui"
import { formatDate, formatDateTime } from "@/utils/date"

type ProductionOrderRow = {
  id: number
  internal_reference_code: string
  material_badge: { key: string; label: string; tone: string }
  company: string
  status: string
  raw_status: string
  created_at: string | null
  expected_at: string | null
  started_at: string | null
  done_at: string | null
  created_by: string
  started_by: string
  done_by: string
  linked_sales_order: {
    id: number
    customer_name: string
    status: string
    status_label: string
    href: string
  } | null
  items_count: number
  items: Array<{ product: string; quantity: string; unit: string }>
  can_edit: boolean
  can_destroy: boolean
}

type OrderDetail = {
  feasible: boolean
  feasible_with_projection: boolean
  projection_covers_shortage: boolean
  shortages: Array<{ product: string; required: string; available: string; shortage: string; unit: string }>
  projected_shortages: Array<{ product: string; required: string; available: string; shortage: string; unit: string }>
  planned_receipts: Array<{ product: string; quantity: string; unit: string; expected_at: string | null; purchase_order_id: number; purchase_order_status: string }>
  can_start: boolean
  can_close: boolean
}

type TimelineStep = {
  id: string
  title: string
  description: string
  icon: "create" | "send" | "receive" | "factory" | "done" | "cancel" | "clock"
  state: "done" | "pending" | "warning" | "danger"
}

type ExportPayload = {
  orders: Array<{ id: number; expected_at: string | null; linked_sales_order_id: number | null; customer_name: string }>
  items: Array<{ product_id: number; sku: string; name: string; unit: string; quantity: string }>
  generated_at: string
}

const props = defineProps<{
  production_orders: ProductionOrderRow[]
  companies: Array<{ id: number; name: string }>
  statuses: Array<{ label: string; value: string }>
  query: {
    q?: string
    company_id?: string
    status?: string
    page: number
    per_page: number
  }
  pagination: {
    page: number
    perPage: number
    total: number
  }
  can_create: boolean
  can_export: boolean
}>()

const { confirm } = useConfirmation()
const notify = useNotifications()

const columns: UiDataTableColumn[] = [
  { key: "internal_reference_code", label: "Referencia" },
  { key: "material_badge", label: "Materiais", align: "center" },
  { key: "company", label: "Empresa" },
  { key: "status", label: "Status", align: "center" },
  { key: "items_count", label: "Itens", align: "center" },
  { key: "expected_at", label: "Previsao", align: "center" },
  { key: "actions", label: "Acoes", align: "right" },
]

const materialBadgeClassByTone: Record<string, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-700",
  info: "border-sky-200 bg-sky-50 text-sky-700",
  warning: "border-amber-200 bg-amber-50 text-amber-700",
  danger: "border-rose-200 bg-rose-50 text-rose-700",
  neutral: "border-slate-200 bg-slate-100 text-slate-600",
}

const statusBadgeClassByStatus: Record<string, string> = {
  pending: "border-slate-200 bg-slate-100 text-slate-700",
  in_progress: "border-amber-200 bg-amber-50 text-amber-700",
  done: "border-emerald-200 bg-emerald-50 text-emerald-700",
  cancelled: "border-rose-200 bg-rose-50 text-rose-700",
}

function materialBadgeClass(tone: string) {
  return materialBadgeClassByTone[tone] ?? materialBadgeClassByTone.neutral
}

function statusBadgeClass(status: string) {
  return statusBadgeClassByStatus[status] ?? statusBadgeClassByStatus.pending
}

const filters: UiDataTableFilter[] = [
  {
    key: "company_id",
    label: "Empresa",
    options: props.companies.map((company) => ({ label: company.name, value: company.id })),
  },
  {
    key: "status",
    label: "Status",
    options: props.statuses,
  },
]

const selectedFilters = reactive<Record<string, string | number | "">>({
  company_id: props.query.company_id ?? "",
  status: props.query.status ?? "",
})

const isDetailsDrawerOpen = ref(false)
const selectedOrder = ref<ProductionOrderRow | null>(null)
const orderDetail = ref<OrderDetail | null>(null)
const isLoadingDetail = ref(false)

async function openDetailsDrawer(order: ProductionOrderRow) {
  selectedOrder.value = order
  orderDetail.value = null
  if (!requiresViabilityDetails(order)) {
    isLoadingDetail.value = false
    isDetailsDrawerOpen.value = true
    return
  }

  isLoadingDetail.value = true
  isDetailsDrawerOpen.value = true

  try {
    const response = await fetch(`/production_orders/${order.id}/detail`, {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
      },
    })

    if (response.ok) {
      orderDetail.value = await response.json()
    }
  } finally {
    isLoadingDetail.value = false
  }
}

function requiresViabilityDetails(order: ProductionOrderRow): boolean {
  return order.raw_status === "pending" || order.raw_status === "in_progress"
}


function productionTimelineSteps(order: ProductionOrderRow): TimelineStep[] {
  const steps: TimelineStep[] = [
    {
      id: "created",
      title: "Ordem criada",
      description: `${formatDateTime(order.created_at)} por ${order.created_by || "Nao informado"}`,
      icon: "create",
      state: "done",
    },
    {
      id: "started",
      title: "Producao iniciada",
      description: order.started_at ? `${formatDateTime(order.started_at)} por ${order.started_by || "Nao informado"}` : "Pendente",
      icon: "factory",
      state: order.started_at ? "done" : "pending",
    },
    {
      id: "done",
      title: "Producao concluida",
      description: order.done_at ? `${formatDateTime(order.done_at)} por ${order.done_by || "Nao informado"}` : "Pendente",
      icon: "done",
      state: order.done_at ? "done" : "pending",
    },
  ]

  if (!order.done_at) {
    steps.push({
      id: "expected",
      title: "Previsao de conclusao",
      description: order.expected_at ? formatDate(order.expected_at) : "Nao informada",
      icon: "clock",
      state: order.expected_at ? "warning" : "pending",
    })
  }

  return steps
}

function fetchWithQuery(overrides: Record<string, string | number | ""> = {}) {
  const params = {
    q: String(overrides.q ?? props.query.q ?? ""),
    company_id: String(overrides.company_id ?? selectedFilters.company_id ?? ""),
    status: String(overrides.status ?? selectedFilters.status ?? ""),
    page: Number(overrides.page ?? props.pagination.page),
    per_page: Number(overrides.per_page ?? props.pagination.perPage),
  }

  router.get("/production_orders", params, {
    preserveState: true,
    preserveScroll: true,
    replace: true,
  })
}

function updateSearch(value: string) {
  fetchWithQuery({ q: value, page: 1 })
}

function updateFilter({ key, value }: { key: string; value: string | number | "" }) {
  selectedFilters[key] = value
  fetchWithQuery({ [key]: value, page: 1 })
}

function changePage(page: number) {
  fetchWithQuery({ page })
}

function changePerPage(perPage: number) {
  fetchWithQuery({ per_page: perPage, page: 1 })
}

function startOrder(id: number) {
  router.patch(`/production_orders/${id}/start`, {}, {
    onSuccess: () => {
      notify.success("Ordem iniciada")

      if (selectedOrder.value?.id === id) {
        selectedOrder.value = {
          ...selectedOrder.value,
          raw_status: "in_progress",
          status: "Em producao",
          started_at: new Date().toISOString(),
        }
      }

      if (orderDetail.value) {
        orderDetail.value = {
          ...orderDetail.value,
          feasible: true,
          shortages: [],
          projected_shortages: [],
          can_start: false,
          can_close: true,
        }
      }

      fetchWithQuery()
    },
  })
}

function closeOrder(id: number) {
  router.patch(`/production_orders/${id}/close`, {}, {
    onSuccess: () => {
      notify.success("Ordem concluida")

      if (selectedOrder.value?.id === id) {
        selectedOrder.value = {
          ...selectedOrder.value,
          raw_status: "done",
          status: "Concluida",
          done_at: new Date().toISOString(),
        }
      }

      if (orderDetail.value) {
        orderDetail.value = {
          ...orderDetail.value,
          can_start: false,
          can_close: false,
        }
      }

      fetchWithQuery()
    },
  })
}

async function destroyOrder(id: number) {
  const ok = await confirm({
    title: "Excluir ordem",
    message: "Deseja excluir esta ordem de producao?",
    type: "danger",
    confirmText: "Excluir",
  })
  if (!ok) return

  router.delete(`/production_orders/${id}`, {
    preserveScroll: true,
    onSuccess: () => notify.success("Ordem removida com sucesso"),
  })
}

const isExportDrawerOpen = ref(false)
const isLoadingExport = ref(false)
const exportPayload = ref<ExportPayload | null>(null)

async function openExportDrawer() {
  isExportDrawerOpen.value = true
  isLoadingExport.value = true

  try {
    const response = await fetch("/production_orders/export_candidates", {
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? "",
      },
    })

    if (response.ok) {
      exportPayload.value = await response.json()
      return
    }

    notify.error("Nao foi possivel carregar os pedidos exportaveis")
  } finally {
    isLoadingExport.value = false
  }
}

function openExportPdf() {
  if (!exportPayload.value || exportPayload.value.items.length === 0) {
    notify.warning("Nao ha itens disponiveis para exportacao.")
    return
  }

  const params = new URLSearchParams()
  if (selectedFilters.company_id) params.set("company_id", String(selectedFilters.company_id))
  if ((props.query.q ?? "").trim()) params.set("q", (props.query.q ?? "").trim())

  const queryString = params.toString()
  const url = queryString ? `/production_orders/export_pdf?${queryString}` : "/production_orders/export_pdf"
  window.open(url, "_blank")
}
</script>

<template>
  <AppShell>
    <div class="space-y-6">
      <section class="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <h1 class="text-2xl font-semibold text-slate-900">Ordens de producao</h1>
        <p class="mt-2 text-sm text-slate-600">Acompanhe producao, viabilidade por estoque e baixa automatica de componentes.</p>
      </section>

      <UiDataTable
        :columns="columns"
        :rows="production_orders"
        :search="query.q ?? ''"
        :filters="filters"
        :selected-filters="selectedFilters"
        :pagination="pagination"
        @update:search="updateSearch"
        @update:filter="updateFilter"
        @page-change="changePage"
        @per-page-change="changePerPage"
      >
        <template #toolbar-actions>
          <UiButton v-if="can_export" size="sm" variant="secondary" @click="openExportDrawer">
            Exportar producao
          </UiButton>

          <Link
            v-if="can_create"
            href="/production_orders/new"
            class="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            Nova ordem
          </Link>
        </template>

        <template #cell="{ row, column, value }">
          <div v-if="column.key === 'actions'" class="flex justify-end gap-2">
            <button
              type="button"
              class="rounded-md border border-slate-300 p-1.5 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
              aria-label="Visualizar detalhes da ordem"
              @click="openDetailsDrawer(row as ProductionOrderRow)"
            >
              <EyeIcon class="size-4" />
            </button>

            <Link
              v-if="(row as ProductionOrderRow).can_edit"
              :href="`/production_orders/${(row as ProductionOrderRow).id}/edit`"
              class="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-100"
            >
              Editar
            </Link>

            <UiButton v-if="(row as ProductionOrderRow).can_destroy" size="sm" variant="danger" @click="destroyOrder((row as ProductionOrderRow).id)">
              Excluir
            </UiButton>
          </div>

          <span v-else-if="column.key === 'expected_at'">{{ formatDate((row as ProductionOrderRow).expected_at) }}</span>

          <div v-else-if="column.key === 'material_badge'" class="text-center">
            <span
              class="inline-flex rounded-full border px-2 py-0.5 text-xs font-medium"
              :class="materialBadgeClass((row as ProductionOrderRow).material_badge.tone)"
            >
              {{ (row as ProductionOrderRow).material_badge.label }}
            </span>
          </div>

          <div v-else-if="column.key === 'status'" class="text-center">
            <span
              class="inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold"
              :class="statusBadgeClass((row as ProductionOrderRow).raw_status)"
            >
              {{ (row as ProductionOrderRow).status }}
            </span>
          </div>

          <span v-else>{{ value || "-" }}</span>
        </template>
      </UiDataTable>

      <UiDrawer v-model="isDetailsDrawerOpen" title="Detalhes da ordem" size="2xl">
        <div v-if="selectedOrder" class="space-y-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-xs uppercase tracking-wide text-slate-500">Ordem de producao</p>
                <h3 class="text-lg font-semibold text-slate-900">{{ selectedOrder.internal_reference_code }}</h3>
                <p class="mt-1 text-sm text-slate-600">Empresa: {{ selectedOrder.company }}</p>
              </div>

              <div class="text-right">
                <span class="inline-flex rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                  {{ selectedOrder.status }}
                </span>
                <template v-if="orderDetail">
                  <p class="mt-2 text-xs" :class="orderDetail.feasible ? 'text-emerald-700' : 'text-rose-700'">
                    {{ orderDetail.feasible ? "Viavel com estoque atual" : "Inviavel com estoque atual" }}
                  </p>
                  <p v-if="orderDetail.projection_covers_shortage" class="mt-1 text-xs text-amber-700">
                    Cobertura prevista por compras/planejamento antes da data.
                  </p>
                </template>
                <p v-else-if="isLoadingDetail" class="mt-2 text-xs text-slate-400">Carregando viabilidade...</p>
                <p v-else-if="!requiresViabilityDetails(selectedOrder)" class="mt-2 text-xs text-emerald-700">Ordem encerrada.</p>
              </div>
            </div>

            <div class="mt-4 overflow-hidden rounded-md border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr>
                    <th class="w-1/4 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Itens</th>
                    <td class="w-1/4 px-3 py-2 text-sm font-semibold text-slate-900">{{ selectedOrder.items_count }}</td>
                    <th class="w-1/4 bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Previsao</th>
                    <td class="w-1/4 px-3 py-2 text-sm font-semibold text-slate-900">{{ formatDate(selectedOrder.expected_at) }}</td>
                  </tr>
                  <tr>
                    <th class="bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Inicio</th>
                    <td class="px-3 py-2 text-sm font-semibold text-slate-900">{{ formatDate(selectedOrder.started_at) }}</td>
                    <th class="bg-slate-50 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Conclusao</th>
                    <td class="px-3 py-2 text-sm font-semibold text-slate-900">{{ formatDate(selectedOrder.done_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="selectedOrder.linked_sales_order" class="mt-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
              <span class="font-medium">Origem:</span>
              Pedido de venda #{{ selectedOrder.linked_sales_order.id }}
              <span class="text-sky-700">({{ selectedOrder.linked_sales_order.status_label }})</span>
              <span v-if="selectedOrder.linked_sales_order.customer_name" class="ml-1 text-sky-700">- {{ selectedOrder.linked_sales_order.customer_name }}</span>
              <Link :href="selectedOrder.linked_sales_order.href" class="ml-2 font-medium underline underline-offset-2">
                Abrir pedido de venda
              </Link>
            </div>
          </section>

          <div v-if="orderDetail" class="mt-3 flex justify-end gap-2">
            <UiButton v-if="orderDetail.can_start" size="sm" variant="secondary" @click="startOrder(selectedOrder.id)">
              Iniciar
            </UiButton>
            <UiButton v-if="orderDetail.can_close" size="sm" variant="primary" @click="closeOrder(selectedOrder.id)">
              Concluir
            </UiButton>
          </div>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Itens planejados</h4>

            <div class="overflow-hidden rounded-md border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <thead class="bg-slate-50 text-slate-600">
                  <tr>
                    <th class="px-3 py-2 text-left font-medium">Produto</th>
                    <th class="px-3 py-2 text-right font-medium">Quantidade</th>
                    <th class="px-3 py-2 text-center font-medium">Unidade</th>
                  </tr>
                </thead>

                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-for="(item, index) in selectedOrder.items" :key="`item-${index}`">
                    <td class="px-3 py-2 text-slate-800">{{ item.product }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ item.quantity }}</td>
                    <td class="px-3 py-2 text-center text-slate-700">{{ item.unit }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p class="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800">
              <CalendarDaysIcon class="size-4 text-slate-500" /> Timeline da ordem
            </p>

            <UiTimeline :steps="productionTimelineSteps(selectedOrder)" />
          </section>

          <section v-if="requiresViabilityDetails(selectedOrder) && isLoadingDetail" class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <p class="py-4 text-center text-sm text-slate-400">Carregando viabilidade de materiais...</p>
          </section>

          <section v-else-if="requiresViabilityDetails(selectedOrder) && orderDetail" class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Viabilidade de materiais</h4>

            <div v-if="orderDetail.feasible" class="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
              Sem faltas de material para concluir esta ordem.
            </div>

            <div v-else class="overflow-hidden rounded-md border border-rose-200">
              <table class="min-w-full divide-y divide-rose-200 text-sm">
                <thead class="bg-rose-50 text-rose-700">
                  <tr>
                    <th class="px-3 py-2 text-left font-medium">Componente</th>
                    <th class="px-3 py-2 text-right font-medium">Necessario</th>
                    <th class="px-3 py-2 text-right font-medium">Disponivel</th>
                    <th class="px-3 py-2 text-right font-medium">Falta</th>
                    <th class="px-3 py-2 text-center font-medium">Unidade</th>
                  </tr>
                </thead>

                <tbody class="divide-y divide-rose-100 bg-white">
                  <tr v-for="(shortage, index) in orderDetail.shortages" :key="`shortage-${index}`">
                    <td class="px-3 py-2 text-slate-800">{{ shortage.product }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ shortage.required }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ shortage.available }}</td>
                    <td class="px-3 py-2 text-right font-semibold text-rose-700">{{ shortage.shortage }}</td>
                    <td class="px-3 py-2 text-center text-slate-700">{{ shortage.unit }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-if="orderDetail.planned_receipts.length > 0" class="mt-4">
              <div class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Viabilidade projetada por pedidos de compra ainda nao recebidos. A ordem so fica 100% viavel quando estas entradas forem efetivamente recebidas.
              </div>

              <p class="mb-2 text-xs font-semibold uppercase tracking-wide text-amber-700">Pedidos que cobrem a falta</p>

              <div class="overflow-hidden rounded-md border border-amber-200">
                <table class="min-w-full divide-y divide-amber-200 text-sm">
                  <thead class="bg-amber-50 text-amber-700">
                    <tr>
                      <th class="px-3 py-2 text-left font-medium">Componente</th>
                      <th class="px-3 py-2 text-right font-medium">Quantidade</th>
                      <th class="px-3 py-2 text-center font-medium">Unidade</th>
                      <th class="px-3 py-2 text-center font-medium">Previsao</th>
                      <th class="px-3 py-2 text-center font-medium">Pedido</th>
                    </tr>
                  </thead>

                  <tbody class="divide-y divide-amber-100 bg-white">
                    <tr v-for="(receipt, index) in orderDetail.planned_receipts" :key="`receipt-${index}`">
                      <td class="px-3 py-2 text-slate-800">{{ receipt.product }}</td>
                      <td class="px-3 py-2 text-right text-slate-700">{{ receipt.quantity }}</td>
                      <td class="px-3 py-2 text-center text-slate-700">{{ receipt.unit }}</td>
                      <td class="px-3 py-2 text-center text-slate-700">{{ formatDate(receipt.expected_at) }}</td>
                      <td class="px-3 py-2 text-center text-slate-700">#{{ receipt.purchase_order_id }} ({{ receipt.purchase_order_status }})</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      </UiDrawer>

      <UiDrawer v-model="isExportDrawerOpen" title="Exportacao de producao" size="2xl">
        <p v-if="isLoadingExport" class="py-4 text-center text-sm text-slate-400">Carregando pedidos viaveis...</p>

        <div v-else-if="exportPayload" class="space-y-4">
          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div class="flex items-center justify-between">
              <div>
                <h4 class="text-sm font-semibold text-slate-900">Consolidado por SKU</h4>
                <p class="text-xs text-slate-500">Gerado em {{ formatDateTime(exportPayload.generated_at) }}</p>
              </div>
              <UiButton
                size="sm"
                variant="primary"
                :disabled="exportPayload.items.length === 0"
                @click="openExportPdf"
              >
                Gerar PDF
              </UiButton>
            </div>

            <p v-if="exportPayload.items.length === 0" class="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              Nao ha itens disponiveis para producao no momento.
            </p>

            <div v-else class="mt-3 overflow-hidden rounded-md border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <thead class="bg-slate-50 text-slate-600">
                  <tr>
                    <th class="px-3 py-2 text-left font-medium">SKU</th>
                    <th class="px-3 py-2 text-left font-medium">Produto</th>
                    <th class="px-3 py-2 text-right font-medium">Quantidade</th>
                    <th class="px-3 py-2 text-center font-medium">Unidade</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-for="item in exportPayload.items" :key="item.product_id">
                    <td class="px-3 py-2 text-slate-800">{{ item.sku }}</td>
                    <td class="px-3 py-2 text-slate-800">{{ item.name }}</td>
                    <td class="px-3 py-2 text-right text-slate-700">{{ item.quantity }}</td>
                    <td class="px-3 py-2 text-center text-slate-700">{{ item.unit }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h4 class="mb-3 text-sm font-semibold text-slate-800">Pedidos aptos para producao</h4>
            <div class="overflow-hidden rounded-md border border-slate-200">
              <table class="min-w-full divide-y divide-slate-200 text-sm">
                <thead class="bg-slate-50 text-slate-600">
                  <tr>
                    <th class="px-3 py-2 text-left font-medium">OP</th>
                    <th class="px-3 py-2 text-left font-medium">Pedido venda</th>
                    <th class="px-3 py-2 text-left font-medium">Cliente</th>
                    <th class="px-3 py-2 text-center font-medium">Previsao</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100 bg-white">
                  <tr v-for="order in exportPayload.orders" :key="order.id">
                    <td class="px-3 py-2 text-slate-800">#{{ order.id }}</td>
                    <td class="px-3 py-2 text-slate-700">{{ order.linked_sales_order_id ? `#${order.linked_sales_order_id}` : '-' }}</td>
                    <td class="px-3 py-2 text-slate-700">{{ order.customer_name || '-' }}</td>
                    <td class="px-3 py-2 text-center text-slate-700">{{ formatDate(order.expected_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </UiDrawer>
    </div>
  </AppShell>
</template>


